// Merge logic for the Ferrari POD bulk-upload flow: a GROUP of parsed POD
// cards becomes ONE job. Pure function so the union/dedup rules are unit-
// testable without the modal (the create flow can't be exercised in the
// browser without live parses).
//
// Rules (from the ROADMAP spec):
//   - stops[] union, deduped by locationId then normalized (name, address).
//     On a dup, the SIGNED card's contact wins; ties go to the most
//     complete contact (name/phone/email count).
//   - packages[] union — every POD image keeps its own package row(s) with
//     its own HAWB. `_cardId` survives so the caller can stamp each row
//     with the photo URL of the form it came from.
//   - Airport stop is ALWAYS the company hub (never the form's DEST.
//     AIRPORT), positioned first on inbound / last on outbound.
//   - Guard count = max across cards (each card already applied the
//     Ferrari 100-lb → 2-guard rule).

function uid() { return Math.random().toString(36).slice(2, 9); }

const norm = (s) => String(s || "").toLowerCase().replace(/\s+/g, " ").trim();

// How many contact fields a stop carries — used to pick which card's
// contact wins when stops dedup inside a group.
function contactScore(stop) {
  return ["contactName", "contactPhone", "contactEmail"]
    .reduce((n, k) => n + (stop?.[k] ? 1 : 0), 0);
}

/**
 * @param {Array} members - parsed POD cards:
 *   { id, jobType, trackingNumber, stop|null, packages, guards,
 *     scheduledDate, scheduledTime, notes, signed: {shipperSigned, deliverySigned} }
 * @param {Object} companyDoc - { hubAirport, hubAirportAddress, hubAirportLat,
 *     hubAirportLng, hubAirportNotes, inboundArrivalTime }
 * @returns one job's worth of fields + { mixedDirection } for the UI warning.
 */
export function mergeGroupCards(members, companyDoc = {}) {
  const jobType = members[0].jobType;
  const mixedDirection = members.some((m) => m.jobType !== jobType);

  const stops = [];
  const stopIdByCard = {};
  for (const m of members) {
    if (!m.stop) continue;
    const key = m.stop.locationId || `${norm(m.stop.locationName)}|${norm(m.stop.address)}`;
    const existing = stops.find((s) => s._key === key);
    if (existing) {
      stopIdByCard[m.id] = existing.id;
      const mSigned = !!(m.signed?.deliverySigned || m.signed?.shipperSigned);
      const better = (mSigned && !existing._contactSigned)
        || (mSigned === existing._contactSigned && contactScore(m.stop) > contactScore(existing));
      if (better) {
        existing.contactName = m.stop.contactName || null;
        existing.contactPhone = m.stop.contactPhone || null;
        existing.contactEmail = m.stop.contactEmail || null;
        existing._contactSigned = mSigned;
      }
      continue;
    }
    const s = {
      id: uid(),
      type: jobType === "inbound" ? "delivery" : "pickup",
      locationId: m.stop.locationId || null,
      locationName: m.stop.locationName || "",
      address: m.stop.address || "",
      lat: typeof m.stop.lat === "number" ? m.stop.lat : null,
      lng: typeof m.stop.lng === "number" ? m.stop.lng : null,
      contactName: m.stop.contactName || null,
      contactPhone: m.stop.contactPhone || null,
      contactEmail: m.stop.contactEmail || null,
      scheduledTime: m.scheduledTime || "",
      _key: key,
      _contactSigned: !!(m.signed?.deliverySigned || m.signed?.shipperSigned),
    };
    stops.push(s);
    stopIdByCard[m.id] = s.id;
  }
  const realStops = stops.map(({ _key, _contactSigned, ...s }) => s);

  const airportStop = {
    id: uid(),
    type: "airport",
    locationName: companyDoc?.hubAirport || "CLT",
    address: companyDoc?.hubAirportAddress || "",
    lat: companyDoc?.hubAirportLat ?? null,
    lng: companyDoc?.hubAirportLng ?? null,
    accessNotes: companyDoc?.hubAirportNotes || "",
    scheduledTime: jobType === "inbound" ? (companyDoc?.inboundArrivalTime || "") : "",
  };
  const jobStops = jobType === "inbound" ? [airportStop, ...realStops] : [...realStops, airportStop];

  const packages = members.flatMap((m) => (m.packages || []).map((p) => ({
    id: uid(), ...p,
    stopId: stopIdByCard[m.id] || realStops[0]?.id || null,
    _cardId: m.id,
  })));

  const dates = members.map((m) => m.scheduledDate).filter(Boolean).sort();
  const notes = [...new Set(members.map((m) => (m.notes || "").trim()).filter(Boolean))].join("\n");
  return {
    jobType,
    mixedDirection,
    jobNumber: members[0].trackingNumber || "",
    scheduledDate: dates[0] || "",
    scheduledTime: members.map((m) => m.scheduledTime).find(Boolean) || "",
    numGuards: Math.max(...members.map((m) => m.guards || 1)),
    stops: jobStops,
    packages,
    notes,
  };
}
